import {
  MathUtils,
  PerspectiveCamera,
  Vector3,
} from 'three';
import { WORLD } from '../config';
import { waterSurfaceAt } from '../world/waveField';
import type { HeightField } from '../world/HeightField';
import type { CharacterAnimator } from './CharacterAnimator';
import type { CharacterInstance } from './CharacterModel';
import type { FishingSystem } from '../fishing/FishingSystem';
import type { PlayerHealth } from '../combat/PlayerHealth';
import type { Weapon } from '../combat/Weapon';
import type { FishingRod } from './FishingRod';

const GRAVITY = -24;
const JUMP_VELOCITY = 8.5;
const WALK_SPEED = 4.2;
const SPRINT_SPEED = 9.5;
const SWIM_SPEED = 3.5;
/**
 * Jak hluboká musí být voda, aby se v ní plavalo.
 *
 * Rozhoduje **hloubka dna**, ne výška hráče. Kdyby se plavání poznávalo jen
 * podle toho, že je postava pod úrovní hladiny, prohlásila by hra za plavce
 * i toho, kdo stojí na suché zemi ležící níž než hladina moře — a takové
 * pevniny je u pobřeží spousta.
 */
const SWIM_DEPTH = 1.4;
/**
 * Jak hluboko pod hladinou se ustálí kořen postavy při plavání.
 *
 * Plavecká animace drží kořen zhruba ve výšce hrudi, takže při téhle
 * hodnotě zůstane hlava i ramena nad vodou — což je to, co odlišuje plavce
 * od někoho, kdo ve vodě strnule visí.
 */
const SWIM_REST_DEPTH = 0.05;
/** Tuhost vztlaku. Vyšší = tvrdší odraz zpátky k hladině. */
const BUOYANCY = 60;
/** Odpor vody. Bez něj by postava u hladiny donekonečna kmitala. */
const WATER_DRAG = 5.5;
/** Jak rychlý dopad už se počítá jako plné šplouchnutí (m/s). */
const FULL_SPLASH_SPEED = 12;
/**
 * Rozestup vln při brodění, v metrech ušlé dráhy.
 *
 * Rozsah, ne jedno číslo: při pevném kroku vzniká za chodcem pravidelná řada
 * stejně vzdálených kruhů, což vypadá jako tisk, ne jako voda.
 */
const WADE_STEP_MIN = 0.7;
const WADE_STEP_MAX = 1.6;
const ROTATION_SMOOTHING = 12.0;
/** Prodlevy zásahu pro jednotlivé seky komba (v sekundách). */
const COMBO_HIT_DELAYS = [0.18, 0.22, 0.36];
/** Minimální prodleva před dalším seknutím. */
const COMBO_COOLDOWNS = [0.36, 0.44, 0.65];
/** Časové okno pro navázání dalšího seku (v sekundách). */
const COMBO_RESET_TIME = 0.95;

export class ThirdPersonControls {
  readonly character: CharacterInstance;
  readonly animator: CharacterAnimator;
  readonly camera: PerspectiveCamera;
  readonly heightField: HeightField;

  private readonly domElement: HTMLElement;
  private readonly keys = new Set<string>();

  // Camera Orbit
  private yaw = 0;
  private pitch = 0.25;
  private cameraDistance = 3.8;
  private targetDistance = 3.8;
  private cameraHeightOffset = 1.6;

  // Character movement physics
  private velocityY = 0;
  private isGrounded = true;
  private isSwimming = false;
  /** Byla postava minulý snímek pod hladinou? Přechod spouští šplouchnutí. */
  private wasUnderwater = false;
  /** Ušlá dráha ve vodě od posledního kruhu na hladině. */
  private wadeDistance = 0;
  /** Volá se při dopadu do vody a při brodění. Napojuje efekt i zvuk. */
  onWaterSplash: ((x: number, z: number, strength: number) => void) | null = null;
  /** Brodění — kromě místa předává i směr chůze, brázda se podle něj natáčí. */
  onWaterWade:
    | ((x: number, z: number, dirX: number, dirZ: number, strength: number) => void)
    | null = null;
  /** Do jaké ušlé dráhy se pustí další vlna. Pokaždé jiná. */
  private wadeStep = WADE_STEP_MIN;
  private moveDirection = new Vector3();
  private characterTargetRotation = 0;
  private locked = false;
  /** Prut a stav rybaření — přiřazuje Engine po vytvoření postavy. */
  rod: FishingRod | null = null;
  fishingSystem: FishingSystem | null = null;
  weapon: Weapon | null = null;
  health: PlayerHealth | null = null;
  /** Zavolá se v okamžiku seknutí, ne při stisku — kvůli načasování zásahu. */
  onSwing: (() => void) | null = null;
  /**
   * Pozastavení pohybu i kamery.
   *
   * Ovládání každý snímek přepisuje natočení postavy a pozici kamery.
   * Při ladění posazení předmětů by tak zápasilo s posuvníky a postava
   * by se cukala a otáčela zpátky.
   */
  paused = false;
  private fishing = false;
  private isMouseDown = false;
  private comboStep = 0;
  private comboResetTimer = 0;
  private attackCooldown = 0;
  private swingPending = 0;

  constructor(
    character: CharacterInstance,
    animator: CharacterAnimator,
    camera: PerspectiveCamera,
    domElement: HTMLElement,
    heightField: HeightField,
  ) {
    this.character = character;
    this.animator = animator;
    this.camera = camera;
    this.domElement = domElement;
    this.heightField = heightField;

    domElement.addEventListener('click', this.onClick);
    domElement.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('wheel', this.onWheel, { passive: true });
  }

  /**
   * Šplouchnutí při dopadu a kruhy při brodění.
   *
   * Dopad se pozná přechodem hlavy pod hladinu, ne dotykem nohou — jinak by
   * se spustil už při vstupu do centimetrové mělčiny. Síla vychází z rychlosti
   * dopadu, takže seskok z útesu vypadá jinak než pomalé vejití do vody.
   */
  private updateSplashes(
    charPos: Vector3,
    groundY: number,
    surfaceY: number,
    isMoving: boolean,
    isSprinting: boolean,
  ): void {
    const waterDepth = WORLD.seaLevel - groundY;
    const inWater = waterDepth > 0.15 && charPos.y < surfaceY;

    if (inWater && !this.wasUnderwater) {
      const impact = Math.min(1, Math.abs(this.velocityY) / FULL_SPLASH_SPEED);
      // I klidné vejití do vody něco udělá, jen málo.
      this.onWaterSplash?.(charPos.x, charPos.z, Math.max(0.18, impact));
      this.wadeDistance = 0;
    }
    this.wasUnderwater = inWater;

    if (!inWater || !isMoving) return;

    // Vlny se pouští po ušlé dráze, ne po čase — jinak by při stání na
    // místě vznikaly pořád dokola a při běhu by se rozestupy roztáhly.
    const step = this.moveDirection.length();
    this.wadeDistance += step;
    if (this.wadeDistance < this.wadeStep) return;
    this.wadeDistance = 0;
    this.wadeStep = WADE_STEP_MIN + Math.random() * (WADE_STEP_MAX - WADE_STEP_MIN);

    // Směr chůze; brázda se podle něj protáhne.
    const dirX = step > 1e-6 ? this.moveDirection.x / step : 0;
    const dirZ = step > 1e-6 ? this.moveDirection.z / step : 0;
    // Kolísání síly, ať nejsou všechny vlny stejně výrazné.
    const base = isSprinting ? 0.55 : 0.35;
    const strength = base * (0.7 + Math.random() * 0.6);
    this.onWaterWade?.(charPos.x, charPos.z, dirX, dirZ, this.isSwimming ? strength * 0.5 : strength);
  }

  get isPointerLocked(): boolean {
    return this.locked;
  }

  placeAt(x: number, z: number): void {
    const groundY = this.heightField.heightAt(x, z);
    const y = Math.max(WORLD.seaLevel, groundY);
    this.character.root.position.set(x, y, z);
    this.characterTargetRotation = this.yaw + Math.PI;
    this.character.root.rotation.y = this.characterTargetRotation;
    this.updateCamera(0.016);
  }

  /**
   * Zdrojem pravdy je stav systému, ne vlastní příznak.
   *
   * Dokud si ovládání drželo svoje `fishing`, stačilo, aby systém skončil
   * jinudy než přes `toggleFishing()`, a příznak zůstal viset — další `F`
   * pak rybaření vyplo místo zaplo. Vlastní příznak zbyl jen pro případ,
   * že systém vůbec není (ranné načítání).
   */
  get isFishing(): boolean {
    return this.fishingSystem ? this.fishingSystem.isActive : this.fishing;
  }

  /**
   * Zapne nebo vypne rybaření.
   *
   * Nahození je jednorázový klip; po jeho dohrání se přejde do smyčky
   * čekání. Doba přechodu odpovídá délce klipu, ne pevnému číslu —
   * jinak by se ruka po výměně animačního balíku rozešla s prutem.
   */
  toggleFishing(): void {
    const system = this.fishingSystem;

    // Ve fázi zábru má F jiný význam: neukončuje rybaření, ale zasekává.
    // Bez toho by hráč reflexivně mačkající F rybu pokaždé pustil.
    if (system?.currentPhase === 'bite') {
      system.hook(this.character, this.animator);
      return;
    }

    if (this.isFishing) {
      this.fishing = false;
      this.rod?.show(false);
      this.weapon?.show(true);
      system?.stop(this.animator);
      return;
    }

    if (system && !system.start(this.character, this.animator)) return;

    this.fishing = true;
    this.rod?.show(true);
    this.weapon?.show(false);
    if (!system) this.animator.playFishing('fish_cast');
  }

  /**
   * @param worldTime společný čas světa; předává se dál rybaření, aby
   *        skákající ryby vyšly u všech hráčů stejně
   */
  update(dt: number, worldTime = performance.now() / 1000): void {
    // Prsty se svírají, jen když je v ruce zbraň. Rybaření si sevření
    // řídí samo přes stav animace.
    this.animator.holdingItem = this.weapon?.visible ?? false;

    if (this.paused) {
      // Animace běžet musí, jinak by postava ztuhla v jednom snímku a
      // posazení meče by se posuzovalo v nesmyslné póze.
      this.animator.update(dt);
      return;
    }

    if (this.health && !this.health.alive) {
      if (this.isFishing) {
        this.fishing = false;
        this.rod?.show(false);
        this.fishingSystem?.stop(this.animator);
      }
      this.animator.update(dt);
      this.updateCamera(dt);
      return;
    }

    if (this.attackCooldown > 0) this.attackCooldown -= dt;
    if (this.swingPending > 0) {
      this.swingPending -= dt;
      if (this.swingPending <= 0) this.onSwing?.();
    }
    if (this.comboResetTimer > 0) {
      this.comboResetTimer -= dt;
      if (this.comboResetTimer <= 0) {
        this.comboStep = 0;
      }
    }

    // Plynulé řetězení útoků při držení levého tlačítka myši
    if (
      this.isMouseDown &&
      this.attackCooldown <= 0 &&
      this.locked &&
      (!this.health || this.health.alive)
    ) {
      this.attack();
    }

    const charPos = this.character.root.position;
    const groundY = this.heightField.heightAt(charPos.x, charPos.z);
    const waterDepth = WORLD.seaLevel - groundY;
    // Skutečná výška hladiny i s vlnou, ne klidová úroveň moře.
    //
    // Dokud se plavalo proti konstantě, jezdila vykreslená voda po postavě
    // nahoru a dolů: na hřebeni se ocitla pod hladinou, v prohlubni visela
    // ve vzduchu. Počítá se to ze stejného spektra, ze kterého se generuje
    // shader (viz waveField.ts), takže se to nemůže rozejít.
    const surfaceY = waterDepth > 0 ? waterSurfaceAt(charPos.x, charPos.z, worldTime, groundY) : WORLD.seaLevel;
    // Plave se jen tam, kde nohy nedosáhnou na dno. V mělčině se brodí.
    // Plave se jen tam, kde nohy nedosáhnou na dno — a rozhodnutí musí mít
    // hysterezi.
    //
    // Rovnovážná hladina plavce leží kousek pod povrchem, tedy přesně tam,
    // kde by ležel i jednoduchý práh. Postava by pak každý druhý snímek
    // překlápěla mezi plaváním a stáním: animace by blikala a fyzika by
    // střídala vztlak s gravitací. Vejít do plavání proto stačí kousek pod
    // hladinu, ale vystoupit z něj až nad ni.
    const enterSwim = charPos.y < surfaceY - 0.05;
    const stayInSwim = charPos.y < surfaceY + 0.35;
    this.isSwimming = waterDepth > SWIM_DEPTH && (this.isSwimming ? stayInSwim : enterSwim);
    // Skok končí i dopadem do vody. Bez toho by příznak skoku zůstal viset
    // napořád (dopad na zem už nikdy nenastane) a s ním i póza ze skoku.
    if (this.isSwimming) this.animator.cancelJump();

    // Movement input
    let inputForward = 0;
    let inputRight = 0;
    if (this.keys.has('KeyW')) inputForward += 1;
    if (this.keys.has('KeyS')) inputForward -= 1;
    if (this.keys.has('KeyD')) inputRight += 1;
    if (this.keys.has('KeyA')) inputRight -= 1;

    const isMoving = inputForward !== 0 || inputRight !== 0;
    // Rozejít se s prutem v ruce nedává smysl — pohyb rybaření ukončí.
    if (this.isFishing && isMoving) this.toggleFishing();
    const isSprinting = (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight')) && isMoving;

    // Jump
    if (this.keys.has('Space') && this.isGrounded && !this.isSwimming) {
      this.velocityY = JUMP_VELOCITY;
      this.isGrounded = false;
      this.animator.triggerJump();
    }

    // Direction relative to camera yaw
    if (isMoving) {
      const inputAngle = Math.atan2(-inputRight, inputForward);
      this.characterTargetRotation = this.yaw + Math.PI + inputAngle;

      const speed = this.isSwimming ? SWIM_SPEED : isSprinting ? SPRINT_SPEED : WALK_SPEED;
      const moveAngle = this.yaw + inputAngle;
      this.moveDirection.set(
        -Math.sin(moveAngle),
        0,
        -Math.cos(moveAngle),
      ).normalize().multiplyScalar(speed * dt);

      charPos.add(this.moveDirection);

      // Clamp within world borders
      const limit = WORLD.size * 0.5 - 2;
      charPos.x = MathUtils.clamp(charPos.x, -limit, limit);
      charPos.z = MathUtils.clamp(charPos.z, -limit, limit);
    }

    // Smooth character rotation towards movement direction
    const currentRot = this.character.root.rotation.y;
    let diff = this.characterTargetRotation - currentRot;
    while (diff < -Math.PI) diff += Math.PI * 2;
    while (diff > Math.PI) diff -= Math.PI * 2;
    this.character.root.rotation.y += diff * Math.min(1, dt * ROTATION_SMOOTHING);

    // Vertical physics & terrain collision
    if (this.isSwimming) {
      // Vztlak, ne přilepení k hladině.
      //
      // Dřív se tady rychlost vynulovala a výška se prostě dotáhla na
      // hladinu. Kdo skočil z útesu, ten se u hladiny zastavil a zůstal
      // na ní stát — vypadalo to, že levituje, a nikdy nenastal okamžik
      // dopadu, na který by šlo pověsit šplouchnutí. Teď si postava
      // svislou rychlost nese s sebou: propadne se pod hladinu, vztlak ji
      // vytlačí zpátky a odpor vody houpání utlumí.
      // Cíl se musí zvednout o průhyb, který způsobí tíha.
      //
      // Vztlak je pružina táhnoucí k hladině, ale gravitace ji pořád tlačí
      // dolů, takže rovnováha nenastane v cíli, nýbrž o GRAVITY/BUOYANCY
      // níž. Bez téhle korekce plave postava o 40 cm hlouběji, než se čeká
      // — hlava zmizí pod hladinou a vypadá to, že ve vodě jen visí.
      const gravitySag = -GRAVITY / BUOYANCY;
      const floatLevel = surfaceY - SWIM_REST_DEPTH + gravitySag;
      const submersion = Math.max(0, floatLevel - charPos.y);
      this.velocityY += (GRAVITY + BUOYANCY * submersion) * dt;
      this.velocityY -= this.velocityY * Math.min(1, WATER_DRAG * dt);
      charPos.y += this.velocityY * dt;
      this.isGrounded = true;
    } else {
      this.velocityY += GRAVITY * dt;
      charPos.y += this.velocityY * dt;

      if (charPos.y <= groundY) {
        charPos.y = groundY;
        this.velocityY = 0;
        if (!this.isGrounded) {
          this.isGrounded = true;
          this.animator.landJump();
        }
      } else {
        this.isGrounded = false;
      }
    }

    // Dno je pevné vždycky, i při plavání.
    //
    // Tahle jediná řádka je rozdíl mezi hrou a propadnutím pod mapu: dokud
    // se clamp prováděl jen v nevodní větvi, stačilo se ocitnout pod
    // hladinou nad mělčinou a postava propadla terénem — a ven už se
    // nedostala, protože pod hladinou pořád "plavala".
    if (charPos.y < groundY) {
      charPos.y = groundY;
      if (this.velocityY < 0) this.velocityY = 0;
      this.isGrounded = true;
    }

    this.updateSplashes(charPos, groundY, surfaceY, isMoving, isSprinting);

    if (this.isFishing) {
      this.fishingSystem?.update(dt, worldTime, this.character, this.animator);
    } else if (this.fishing) {
      // Systém skončil sám (třeba po chybě) — srovnat i prut a příznak.
      this.fishing = false;
      this.rod?.show(false);
    }

    // Update animations
    //
    // Během seknutí se lokomoce nesmí přepsat — setMovementState se volá
    // každý snímek a animaci útoku by okamžitě zrušil dřív, než se meč
    // vůbec pohne. Přesně proto útok "nefungoval".
    if (!this.isFishing && this.attackCooldown <= 0) {
      this.animator.setMovementState(isMoving, isSprinting, this.isSwimming);
    }
    this.animator.update(dt);

    // Update Third-Person Camera
    this.updateCamera(dt);
  }

  private updateCamera(dt: number): void {
    const charPos = this.character.root.position;
    const targetLookAt = new Vector3(
      charPos.x,
      charPos.y + this.cameraHeightOffset,
      charPos.z,
    );

    this.cameraDistance = MathUtils.damp(this.cameraDistance, this.targetDistance, 10, dt);

    // Spherical offset
    const cosPitch = Math.cos(this.pitch);
    const offsetX = Math.sin(this.yaw) * cosPitch * this.cameraDistance;
    const offsetZ = Math.cos(this.yaw) * cosPitch * this.cameraDistance;
    const offsetY = Math.sin(this.pitch) * this.cameraDistance;

    const camPos = new Vector3(
      targetLookAt.x + offsetX,
      targetLookAt.y + offsetY,
      targetLookAt.z + offsetZ,
    );

    // Collision with terrain to prevent camera dipping below ground
    const camGround = this.heightField.heightAt(camPos.x, camPos.z);
    const minCamY = Math.max(camGround + 0.35, WORLD.seaLevel + 0.2);
    if (camPos.y < minCamY) {
      camPos.y = minCamY;
    }

    this.camera.position.copy(camPos);
    this.camera.lookAt(targetLookAt);
  }

  private onClick = (): void => {
    if (this.locked) return;
    if (this.health && !this.health.alive) return;
    const request = this.domElement.requestPointerLock() as unknown;
    if (request instanceof Promise) {
      request.catch(() => {});
    }
  };

  private onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.domElement;
    if (!this.locked) {
      this.keys.clear();
      this.isMouseDown = false;
    }
    const hint = document.getElementById('click');
    if (hint) {
      const isDead = Boolean(this.health && !this.health.alive);
      hint.classList.toggle('hidden', this.locked || isDead);
      if (!this.locked && !isDead) hint.textContent = 'Klikni pro ovládání postavy';
    }
  };

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) return;
    this.yaw -= event.movementX * 0.0024;
    this.pitch = MathUtils.clamp(
      this.pitch + event.movementY * 0.0024,
      -0.2,
      1.2,
    );
  };

  /**
   * Seknutí mečem s 3-stupňovým kombem:
   * Sek 1 -> Sword_Regular_A
   * Sek 2 -> Sword_Regular_B
   * Sek 3 -> Sword_Regular_C
   */
  attack(): void {
    if (this.attackCooldown > 0 || this.isFishing) return;
    if (this.health && !this.health.alive) return;

    const step = this.comboStep;
    this.animator.playAttack(step);

    this.swingPending = COMBO_HIT_DELAYS[step] ?? 0.2;
    this.attackCooldown = COMBO_COOLDOWNS[step] ?? 0.4;
    this.comboResetTimer = COMBO_RESET_TIME;

    // Posuneme kombo: 0 -> 1 -> 2 -> 0
    this.comboStep = (this.comboStep + 1) % 3;
    this.weapon?.show(true);
  }

  /**
   * Levé tlačítko seká — ale jen když je kurzor zamčený. První kliknutí
   * do okna zámek teprve zapíná a seknout by při něm bylo matoucí.
   */
  private onMouseDown = (event: MouseEvent): void => {
    if (event.button !== 0 || !this.locked) return;
    if (this.health && !this.health.alive) return;
    this.isMouseDown = true;
    this.attack();
  };

  private onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) {
      this.isMouseDown = false;
    }
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    if (this.health && !this.health.alive) return;
    if (event.code === 'KeyF') {
      this.toggleFishing();
    }
    if (event.code === 'KeyX') {
      this.animator.toggleDance();
    }
    if (event.code === 'Space') {
      event.preventDefault();
    }
    this.keys.add(event.code);
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  private onWheel = (event: WheelEvent): void => {
    this.targetDistance = MathUtils.clamp(
      this.targetDistance + event.deltaY * 0.005,
      1.8,
      8.0,
    );
  };

  dispose(): void {
    this.domElement.removeEventListener('click', this.onClick);
    this.domElement.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('wheel', this.onWheel);
    this.animator.dispose();
    this.character.dispose();
  }
}
